import { z } from "zod";

export type ValidationErrors = Record<string, ValidationError>;

export class ValidationResult {
  constructor(
    readonly success: boolean,
    readonly validationErrors?: ValidationErrors,
  ) {}
}

export type ValidationException = ValidationResult & {
  success: false;
};

export class ValidationError extends Error {
  readonly fullPath: string;

  constructor(message: string, fullPath?: string) {
    super(message);
    this.fullPath = fullPath || "";
  }

  static fromZodIssue(issue: z.core.$ZodIssue) {
    return new ValidationError(issue.message ?? "", issue.path.join("_"));
  }
}

/**
 * Converts Zod issues to validation errors
 * @param issues Zod issues
 * @returns Validation errors
 */
function zodIssuesToValidationErrors(
  issues: z.core.$ZodIssue[],
): ValidationErrors {
  return issues.reduce((acc, issue) => {
    const validationError = ValidationError.fromZodIssue(issue);
    acc[validationError.fullPath] = validationError;
    return acc;
  }, {} as ValidationErrors);
}

/**
 * Validates an object against a schema and throws a `ValidationResult` if validation fails.
 * @param schema Schema to validate against
 * @param obj Object to validate
 */
export function validateSchema<T extends z.ZodTypeAny>(
  schema: T,
  obj: unknown,
): z.infer<T> {
  const zodParseResult = schema.safeParse(obj);

  if (!zodParseResult.success) {
    throw new ValidationResult(
      false,
      zodIssuesToValidationErrors(zodParseResult.error.issues),
    );
  }

  return zodParseResult.data;
}

/**
 * Validates an object against a schema and returns a `ValidationResult` if validation fails.
 * @param schema Schema to validate against
 * @param obj Object to validate
 * @returns Validation result
 */
export function safeValidateSchema<T = object>(
  schema: z.ZodType,
  obj: T,
): ValidationResult {
  const zodParseResult = schema.safeParse(obj);

  return new ValidationResult(
    zodParseResult.success,
    !zodParseResult.success
      ? zodIssuesToValidationErrors(zodParseResult.error.issues)
      : {},
  );
}

/**
 * Checks if the given error is a validation exception and returns any validation errors
 * @param e Unkown exception
 * @returns Validation errors or undefined
 */
export function extractValidationErrors(
  e: unknown,
): ValidationErrors | undefined {
  if (e instanceof ValidationResult) {
    console.warn("Validation error was thrown", e);
    return (e as ValidationResult).validationErrors;
  }
}

export function mergeValidationResults(validationResults: ValidationResult[]) {
  const mergedValidationErrors = validationResults.reduce((acc, result) => {
    return { ...acc, ...result.validationErrors };
  }, {});
  return new ValidationResult(false, mergedValidationErrors);
}

export function throwIfAnyValidationFails(
  validationResults: ValidationResult[],
) {
  if (validationResults.some((result) => !result.success)) {
    const mergedValidationResult = mergeValidationResults(validationResults);
    throw mergedValidationResult;
  }
}
